import { IntlShape } from 'react-intl';

import { Line } from '../../orders/models/Line';
import { RecipeReasonModel } from '../models/QuickSaleModel';

/** DRF errors, nested serializers included, as readable lines. */
export function flattenErrors(detail: unknown, path = ''): string[] {
  if (detail === null || detail === undefined) {
    return [];
  }
  if (Array.isArray(detail)) {
    return detail.flatMap((item) => flattenErrors(item, path));
  }
  if (typeof detail === 'object') {
    return Object.entries(detail as Record<string, unknown>)
      .filter(([field]) => field !== 'error_codes')
      .flatMap(([field, value]) =>
        flattenErrors(value, path ? `${path}.${field}` : field)
      );
  }
  return [path ? `${path}: ${detail}` : String(detail)];
}

export function apiErrorMessage(error: any, fallback: string): string {
  const lines = flattenErrors(error?.response?.data);
  return lines.length ? lines.join(' · ') : fallback;
}

export function formatEuro(intl: IntlShape, amount?: number | null): string {
  if (amount === null || amount === undefined) {
    return '-';
  }
  return intl.formatNumber(amount, { style: 'currency', currency: 'EUR' });
}

/**
 * One reason as a short sales argument. The backend sends codes and names;
 * the wording lives in es.json.
 */
export function formatReason(
  intl: IntlShape,
  reason: RecipeReasonModel
): string {
  const ingredients = reason.ingredients?.length
    ? ` (${reason.ingredients.join(', ')})`
    : '';

  return (
    intl.formatMessage(
      { id: `quickSaleReason.${reason.code}` },
      {
        objective: reason.objective,
        healthIssue: reason.health_issue,
        component: reason.component,
        benefit: reason.benefit,
        nutritionFact: reason.nutrition_fact,
        months: reason.months,
      }
    ) + ingredients
  );
}

/** "5x Porkilicious (800 g)" for each product and extra line. */
export function orderLinesSummary(lines?: Line[]): string[] {
  return (lines || [])
    .filter((line) => line.kind === 'product' || line.kind === 'extra')
    .map((line) => {
      const product = line?.product as any;
      const name =
        product?.product?.name ||
        product?.product?.related_meal?.name ||
        line?.name ||
        '-';
      const traits = product?.traits ? ` (${product.traits})` : '';
      return `${line.units}x ${name}${traits}`;
    });
}

/** Grams per day for a customer multiplier, from the current figure. */
export function gramsForMultiplier(
  currentGrams: number,
  currentMultiplier: number,
  multiplier: number
): number {
  const currentFactor = 1 + currentMultiplier / 100;
  if (currentFactor <= 0) {
    return 0;
  }
  return Math.round((currentGrams * (1 + multiplier / 100)) / currentFactor);
}
